import {
  Checkbox,
  Empty,
  InputNumber,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  notification,
} from "antd";
import { get, has, isNil, keyBy, omit, sum, uniq, values } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import {
  DISPATCH_ROUTES,
  ORGANIZATION_HOSTINGS,
  ORGANIZATION_TYPES,
} from "utils/constants";
import { notifyFailure, run } from "../Organizations/Detail/hooks";

const FULL_SHARE = 100;

const TRACERS_TAKE = 1000;

const receivedOf = (target) => sum(values(get(target, "counts") || {}));

const formOf = (dispatcher) => ({
  name: get(dispatcher, "name") || "",
  active: Boolean(get(dispatcher, "active")),
  routes: get(dispatcher, "routes") || [],
  sources: get(dispatcher, "sources") || [],
  shares: Object.fromEntries(
    (get(dispatcher, "targets") || []).map((target) => [
      get(target, "organization.id"),
      target.share,
    ]),
  ),
});

const DispatcherSettings = ({ dispatcher, names, onCancel, onSaved }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DISPATCHER);
  const { user } = useContext(StoreContext);
  const organizationId = get(user, "organization.id");
  const [form, setForm] = useState(formOf(dispatcher));
  const [candidate, setCandidate] = useState();
  const [receiver, setReceiver] = useState();

  const childrenQuery = useQuery(graphql.queries.DISPATCH_CHILDREN, {
    fetchPolicy: "network-only",
  });
  const candidatesQuery = useQuery(graphql.queries.DISPATCH_ORGANIZATIONS, {
    fetchPolicy: "network-only",
    variables: {
      where: {
        type: ORGANIZATION_TYPES.INS,
        hosting: ORGANIZATION_HOSTINGS.INTERNAL,
        createdByOrganizationId: null,
      },
    },
  });
  const children = get(childrenQuery, "data.dispatchChildren") || [];
  const candidates = (
    get(candidatesQuery, "data.organizations.data") || []
  ).filter(({ id }) => id !== organizationId);
  const sourcesQuery = useQuery(graphql.queries.DISPATCH_SOURCES, {
    fetchPolicy: "network-only",
    variables: {
      take: TRACERS_TAKE,
      where: { ownedBy: { id: organizationId } },
    },
  });
  const [addDispatcher, { loading: adding }] = useMutation(
    graphql.mutations.ADD_DISPATCHER,
  );
  const [updateDispatcher, { loading: updating }] = useMutation(
    graphql.mutations.UPDATE_DISPATCHER,
  );
  const [linkChildOrganization, { loading: linking }] = useMutation(
    graphql.mutations.LINK_CHILD_ORGANIZATION,
  );

  const errors = t("ERRORS", { returnObjects: true });
  const onFailure = notifyFailure({ ERRORS: errors });
  const onLinkFailure = notifyFailure({
    ERRORS: { ...errors, NOT_FOUND: t("LINK.NOT_FOUND") },
  });
  const setField = (key) => (value) =>
    setForm((previous) => ({ ...previous, [key]: value }));
  const targets = keyBy(get(dispatcher, "targets") || [], "organization.id");
  const shareOf = (id) => get(form.shares, id, 0) || 0;
  const attached = children.filter(({ id }) => has(form.shares, id));
  const detached = children.filter(({ id }) => !has(form.shares, id));
  const total = sum(attached.map(({ id }) => shareOf(id)));
  const setShares = (update) =>
    setForm((previous) => ({ ...previous, shares: update(previous.shares) }));
  const attach = () => {
    setShares((shares) => ({ ...shares, [receiver]: 0 }));
    setReceiver();
  };
  const detach = (id) => setShares((shares) => omit(shares, id));
  const sourceOptions = uniq([
    ...(get(sourcesQuery, "data.tracers.data") || [])
      .map((tracer) => get(tracer, "source.name"))
      .filter((name) => !isNil(name)),
    ...form.sources,
  ]).map((name) => ({ value: name, label: name }));

  const save = () => {
    const data = {
      name: form.name,
      active: form.active,
      routes: form.routes,
      sources: form.sources,
      targets: attached.map(({ id }) => ({
        organization: { id },
        share: shareOf(id),
      })),
    };
    const request = isNil(dispatcher)
      ? run(addDispatcher, { variables: { data } })
      : run(updateDispatcher, {
          variables: { where: { id: dispatcher.id }, data },
        });
    return request
      .then(() => {
        notification.success({ message: t("SAVED") });
        return onSaved();
      })
      .catch(onFailure);
  };

  const link = () => {
    const picked = candidates.find(({ id }) => id === candidate);
    Modal.confirm({
      title: t("LINK.CONFIRM_TITLE", { ...names, name: get(picked, "name") }),
      content: t("LINK.CONFIRM_TEXT"),
      okText: t("LINK.CONFIRM"),
      cancelText: t("LINK.CANCEL"),
      onOk: () =>
        run(linkChildOrganization, {
          variables: { where: { id: candidate } },
        })
          .then(() => {
            notification.success({ message: t("LINK.DONE") });
            setCandidate();
            return Promise.all([
              childrenQuery.refetch(),
              candidatesQuery.refetch(),
            ]);
          })
          .catch(onLinkFailure),
    });
  };

  const columns = [
    {
      title: t("COLUMNS.ORGANIZATION"),
      dataIndex: "name",
    },
    {
      title: t("COLUMNS.SHARE"),
      key: "share",
      render: (_value, { id }) => (
        <InputNumber
          min={0}
          max={FULL_SHARE}
          precision={0}
          value={shareOf(id)}
          onChange={(share) =>
            setShares((shares) => ({ ...shares, [id]: share || 0 }))
          }
        />
      ),
    },
    {
      title: t("COLUMNS.RECEIVED"),
      key: "received",
      render: (_value, { id }) => receivedOf(get(targets, id)),
    },
    {
      title: t("COLUMNS.ENVIRONMENT"),
      key: "environment",
      render: (_value, { hosting, environmentReady, environmentUrl }) => {
        if (hosting === ORGANIZATION_HOSTINGS.INTERNAL)
          return (
            <Tooltip title={t("ENVIRONMENT.LOCAL_HINT")}>
              <Tag color="orange">{t("ENVIRONMENT.LOCAL")}</Tag>
            </Tooltip>
          );
        if (environmentReady) return <Tag color="green">{environmentUrl}</Tag>;
        return <Tag color="orange">{t("ENVIRONMENT.MISSING")}</Tag>;
      },
    },
    {
      key: "detach",
      align: "right",
      render: (_value, { id }) => (
        <Tooltip title={t("FORM.DETACH")}>
          <BiTrash
            size={18}
            className="dispatchers__icon"
            aria-label={t("FORM.DETACH")}
            onClick={() => detach(id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="dispatchers">
      <Input
        label={t("FORM.NAME")}
        placeholder={t("FORM.NAME_PLACEHOLDER")}
        value={form.name}
        onChange={({ target: { value } }) => setField("name")(value)}
      />
      <div className="dispatchers__row">
        <Switch checked={form.active} onChange={setField("active")} />
        <span>{t("FORM.ACTIVE")}</span>
      </div>
      <div className="dispatchers__block">
        <strong>{t("FORM.ROUTES")}</strong>
        <Checkbox.Group
          value={form.routes}
          options={Object.values(DISPATCH_ROUTES).map((route) => ({
            value: route,
            label: t(`ROUTE.${route}`),
          }))}
          onChange={setField("routes")}
        />
      </div>
      <div className="dispatchers__block">
        <strong>{t("FORM.SOURCES")}</strong>
        <span className="dispatchers__hint">{t("FORM.SOURCES_HINT")}</span>
        <Select
          mode="tags"
          className="dispatchers__select"
          placeholder={t("FORM.SOURCES_PLACEHOLDER")}
          value={form.sources}
          options={sourceOptions}
          loading={sourcesQuery.loading}
          onChange={setField("sources")}
        />
      </div>
      <div className="dispatchers__block">
        <strong>{t("FORM.TARGETS")}</strong>
        <span className="dispatchers__hint">
          {t("FORM.TARGETS_HINT", names)}
        </span>
        {attached.length === 0 ? (
          <Empty description={t("FORM.NO_TARGETS")} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={attached}
            loading={childrenQuery.loading}
            pagination={false}
            footer={() => (
              <span
                className={
                  total === FULL_SHARE
                    ? "dispatchers__total"
                    : "dispatchers__total dispatchers__total--invalid"
                }
              >
                {t("FORM.TOTAL", { total })}
              </span>
            )}
          />
        )}
        <div className="dispatchers__row">
          <Select
            showSearch
            optionFilterProp="label"
            className="dispatchers__select"
            placeholder={
              children.length === 0
                ? t("FORM.NO_CHILDREN", names)
                : t("FORM.ATTACH_PLACEHOLDER", names)
            }
            value={receiver}
            disabled={detached.length === 0}
            loading={childrenQuery.loading}
            options={detached.map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
            onChange={setReceiver}
          />
          <Button
            type="primary--outlined"
            onClick={attach}
            disabled={isNil(receiver)}
          >
            {t("FORM.ATTACH")}
          </Button>
        </div>
      </div>
      <div className="dispatchers__block">
        <strong>{t("LINK.TITLE")}</strong>
        <span className="dispatchers__hint">{t("LINK.HINT", names)}</span>
        <div className="dispatchers__row">
          <Select
            showSearch
            optionFilterProp="label"
            className="dispatchers__select"
            placeholder={t("LINK.PLACEHOLDER")}
            value={candidate}
            options={candidates.map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
            onChange={setCandidate}
          />
          <Button
            type="primary--outlined"
            onClick={link}
            disabled={isNil(candidate) || linking}
          >
            {t("LINK.ACTION")}
          </Button>
        </div>
      </div>
      <div className="dispatchers__actions">
        <Button type="primary--outlined" onClick={onCancel}>
          {t("FORM.CANCEL")}
        </Button>
        <Button
          type="primary"
          onClick={save}
          disabled={
            adding ||
            updating ||
            childrenQuery.loading ||
            !isNil(childrenQuery.error)
          }
        >
          {t("FORM.SAVE")}
        </Button>
      </div>
    </div>
  );
};

export default DispatcherSettings;
